/**
 * Shared utilities for comparison components.
 *
 * Centralises line-item sorting helpers so that future tweaks
 * (handling '1a', leading zeros, etc.) only need a single change.
 */

/**
 * Parse a lineItemNumber string into a numeric value for sorting.
 * Returns `Infinity` for null / undefined / empty / non-numeric values
 * so that unparseable items sort to the end.
 */
export const parseLineItemNum = (num: string | null | undefined): number => {
    if (num == null || num === '') return Infinity;
    const parsed = parseInt(num, 10);
    return isNaN(parsed) ? Infinity : parsed;
};

/**
 * Safe comparator for line items.
 * Sorts by lineItemNumber ascending, falling back to lineItemId to
 * guarantee a stable order.  Avoids `NaN` that would result from
 * `Infinity - Infinity`.
 */
export const compareLineItems = (
    a: { lineItemNumber?: string | null; lineItemId: number },
    b: { lineItemNumber?: string | null; lineItemId: number }
): number => {
    const numA = parseLineItemNum(a.lineItemNumber);
    const numB = parseLineItemNum(b.lineItemNumber);
    if (numA !== numB) return numA - numB;
    return a.lineItemId - b.lineItemId;
};
